import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ColDef, XGrid, PageChangeParams, ValueFormatterParams, useApiRef } from '@material-ui/x-grid';
import Fetcher, { URLBuild } from '../../services/fetcher';
import settings from '../../settings';
import { Button, Fade, Tooltip } from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { useNotification } from '../../contexts/Notification';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flex: 1
  },
  moreInformationsButton: {
    display: 'flex',
	  justifyContent: 'center',
	  alignItems: 'center',
	  alignContent: 'center'
  }
}));

function dateValueFormatter(params: ValueFormatterParams): string {
  const date: Date = new Date(params.value as number * 1000);
  const day = ('0' + date.getDate()).substr(-2);
  const month = ('0' + (date.getMonth() + 1)).substr(-2);
  const year = date.getFullYear();
  return day + '-' + month + '-' + year;
}

function redirectStripe(domain: string, id: string, display: string = id): JSX.Element {
  return (
    <div>
      <Button style={{textTransform: 'none'}} onClick={() => {
        window.open(settings.PaymentProvider.stripeDashboardUrl + domain + '/' + id, "_blank")
      }}>
        {display}
      </Button>
    </div>
  )
}

interface Customer {
  id: string,
  name: string,
  email: string
};

function renderPaymentId(params: ValueFormatterParams): JSX.Element {
  return redirectStripe('payments', params.value as string);
}

function renderCustomerId(params: ValueFormatterParams): JSX.Element {
  const customer: Customer = params.value as Customer;
  if (customer !== null)
    return redirectStripe('customers', customer.id);
  return <></>
  
}

function renderCustomerName(params: ValueFormatterParams): JSX.Element {
  const customer: Customer = params.value as Customer;
  if (customer !== null)
    return redirectStripe('customers', customer.id, customer.name);
  return <></>
}

function renderCustomerMail(params: ValueFormatterParams): JSX.Element {
  const customer: Customer = params.value as Customer;
  if (customer !== null)
    return redirectStripe('customers', customer.id, customer.email);
  return <></>
}

function amountFormatter(params: ValueFormatterParams): string {
  const amount = (params.value as any).amount as number;
  const currency = (params.value as any).currency as string;
  const left = Math.trunc(amount / 100);
  const rightTmp = amount % 100;
  const right = rightTmp < 10 ? '0' + rightTmp : rightTmp.toString();
  return left + ',' + right + ' ' + currency;
}

function renderInvoiceDownloadButton(params: ValueFormatterParams): JSX.Element {
  const url = params.value as string | null;
  const buttonJsx = (
    <Button
      style={{textTransform: 'none'}}
      onClick={() => {window.open(url as string)}}
      disabled={url === null}
    >
      <CloudDownloadIcon />
    </Button>
  );
  if (url === null) {
    return (
      <Tooltip title="Facture indisponible" TransitionComponent={Fade}>
        <span>
          {buttonJsx}
        </span>
      </Tooltip>
    );
  }
  return buttonJsx
}

export const columns: ColDef[] = [
  { field: 'id', headerName: 'ID Payment', width: 250, sortable: false, renderCell: renderPaymentId },
  { field: 'idCustomer', headerName: 'ID Client Stripe', width: 200, sortable: false, renderCell: renderCustomerId },
  { field: 'name', headerName: 'Nom complet', width: 300, sortable: false, renderCell: renderCustomerName },
  { field: 'email', headerName: 'E-mail', width: 300, sortable: false, renderCell: renderCustomerMail },
  { field: 'date', headerName: 'Date', width: 125, sortable: false, valueFormatter: dateValueFormatter },
  { field: 'amount', headerName: 'Montant', width: 125, sortable: false, valueFormatter: amountFormatter },
  { field: 'description', headerName: 'Description', width: 300, sortable: false },
  { field: 'paymentWay', headerName: 'Moyen', width: 125, sortable: false },
  { field: 'status', headerName: 'Status', width: 200, sortable: false },
  { field: 'invoicePdf', headerName: 'Facture', width: 90, sortable: false, renderCell: renderInvoiceDownloadButton }
];

export interface RowDef {
  id: string,
  idCustomer: Customer,
  name: Customer,
  email: Customer,
  date: number,
  amount: {amount: number, currency: string },
  description: string,
  paymentWay: string,
  status: string,
  invoicePdf: string | null
};

interface Props {
  filters: any
}

const Elements = (props: Props) => {
  const classes = useStyles();
  const limit = 15;
  const [rows, setRows] = useState<RowDef[]>([]);
  const [nbRows, setNbRows] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [prevPage, setPrevPage] = useState<number>(1);
  const [params, setParams] = useState<any>({});
  const apiRef = useApiRef();
  const notification = useNotification();

  useEffect(() => {
    if (JSON.stringify(params) === JSON.stringify(props.filters))
      return;
    apiRef.current.setPage(1);
    setParams({...props.filters});
  }, [props.filters]);

  useEffect(() => {
    setLoading(true);
    Fetcher.get(
      URLBuild(settings.PaymentProvider.paymentApi + '/stripe/payment-intents/list', {
        limit,
        expand: ['data.customer', 'data.invoice'],
        include: ['total_count'],
        ...params
      })
    ).then(data => {
      setNbRows(data.total_count);
      setRows(
        data.data.map((row: any) => {
          return {
            id: row.id,
            idCustomer: row.customer,
            name: row.customer,
            email: row.customer,
            date: row.created,
            amount: {amount: row.amount, currency: row.currency },
            description: row.description,
            paymentWay: row.payment_method_types[0],
            status: row.status,
            invoicePdf: !!row.invoice && !!row.invoice.invoice_pdf ? row.invoice.invoice_pdf : null
          };
        })
      );
      setLoading(false);
    }).catch((e: Error) => {
      notification.notify(e.message, "error");
      console.error(e);
      setLoading(false);
    });
  }, [params]);

  const onPageChange = (paginationInfos: PageChangeParams) => {
    if (paginationInfos.rowCount === 0)
      return;
    setParams((prevParams: any) => {
      delete prevParams['starting_after'];
      delete prevParams['ending_before'];
      if (prevPage !== paginationInfos.page && rows.length !== 0) {
        if (prevPage < paginationInfos.page) {
          params['starting_after'] = rows[rows.length - 1].id;
        } else if (prevPage > paginationInfos.page) {
          params['ending_before'] = rows[0].id;
        }
      }
      return {...prevParams};
    });
    setPrevPage(paginationInfos.page);
  };

  return (
    <div className={classes.root}>
      <XGrid
        rows={rows}
        columns={columns}
        pageSize={limit}
        rowCount={nbRows}
        paginationMode="server"
        pagination={true}
        onPageChange={onPageChange}
        loading={loading}
        hideFooterPagination={loading}
        apiRef={apiRef}
        // showColumnRightBorder={true}
        // showCellRightBorder={true}
      />
    </div>
  );
};

export default Elements